import { NgZone } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * Obtains a verification id for one send. The caller supplies this because the surfaces
 * reach Firebase differently: a multi-factor resolver for a sign-in or reauthentication
 * challenge, a session plus phone number when enrolling a new factor. The verifier handed
 * in is freshly rendered - a reCAPTCHA token is single-use, so a verifier that already
 * completed a send would offer Firebase its consumed cached token.
 */
export type MfaCodeSender = (recaptchaVerifier: any) => Promise<string>;

/**
 * How long Resend Code stays closed after a code has gone out.
 *
 * This paces the user away from Firebase's anti-abuse throttle rather than reacting to it.
 * That throttle is keyed on the device and the target number, has no published duration, and
 * the app cannot clear it (Bug 47480); worse, each further request extends the block instead
 * of recovering from it (Bug 47805). QA measured the block landing at eight resends (Idea
 * 48139). The usual way a user gets there is honest - the text is slow, so they press the
 * button again - and thirty seconds turns eight clicks from a few seconds of impatience into
 * three and a half minutes of deliberate effort.
 *
 * Thirty rather than sixty because a code that genuinely failed to arrive should not strand
 * someone for a minute; SMS that is coming at all is normally there inside fifteen seconds.
 */
export const RESEND_COOLDOWN_SECONDS = 30;

export interface MfaSendOptions {

  /** The masked number to show the user, where the surface has one to show. */
  phoneHint?: string;

  /**
   * The challenge's epoch as it stood *before* whatever await precedes this send - a
   * password reauthentication round-trip, typically. Capture it early and pass it here: if
   * the user cancelled while that await was out, the send is refused outright rather than
   * texting a code they have no way to use.
   */
  epoch?: number;
}

/**
 * One SMS challenge: the state a code step needs, plus the Firebase quirks that come with
 * it. Constructed by hand rather than injected, because a component can be running more
 * than one - the two-factor panel keeps separate enrollment and removal challenges - and
 * each needs its own state.
 *
 * What it owns, so that no surface has to own it again:
 *
 * - a fresh reCAPTCHA per send, released when the challenge ends;
 * - NgZone re-entry, since Firebase resolves the phone and reCAPTCHA promises outside the
 *   zone and the view will not update without it;
 * - a generation counter, so a send completing after the panel was cancelled cannot
 *   re-deposit its state into a closed - or reopened - form;
 * - retiring the previous verification id before a resend, so a code typed while the
 *   resend is in flight cannot be checked against an id Firebase has already replaced.
 *
 * Error mapping deliberately stays with the caller: each surface has its own message field
 * and its own fallback wording. A send rejects with the raw Firebase error for the caller
 * to map - unless the challenge was abandoned while the send was out, in which case the
 * failure belongs to a form nobody is looking at and is swallowed.
 */
export class MfaChallenge {

  public codeSent = false;

  public verificationCode: string;

  public phoneHint: string;

  public sendInProgress = false;

  private verificationId: string;

  private sender: MfaCodeSender;

  private recaptchaVerifier: any;

  private generation = 0;

  /**
   * Set when a send is rejected by Firebase's anti-abuse throttle. Terminal for the life of
   * the challenge: the app cannot clear that throttle and has no way to know when it lifts,
   * so every further send would only be rejected again - and each rejected request extends
   * the block rather than recovering from it (Bug 47805). Cleared by reset(), so a challenge
   * the user deliberately restarts is free to try once more.
   */
  private throttled = false;

  private cooldownDeadline = 0;

  private cooldownTimer: any = null;

  private cooldownShown = 0;

  /**
   * Seconds left before Resend Code reopens, or 0 when it is available.
   *
   * Computed from the deadline on every read rather than counted down into a field. A stored
   * number is only as current as the last tick that ran, and browsers clamp timers hard in a
   * backgrounded tab - so the stored version would let a user come back to a page still
   * claiming a wait that expired minutes ago. The interval below exists to redraw this, not
   * to maintain it.
   */
  public get cooldownRemaining(): number {
    return Math.max(0, Math.ceil((this.cooldownDeadline - Date.now()) / 1000));
  }

  /**
   * The reCAPTCHA host is read per send rather than held, so this class stays clear of the
   * component's view lifecycle - the element only has to exist at the moment of a send.
   */
  constructor(private authService: AuthService,
              private ngZone: NgZone,
              private host: () => HTMLElement) { }

  /**
   * True once a code is actually out. Gates the submit button, and stays false while a
   * resend is replacing the previous, invalidated id.
   */
  public get codeReady(): boolean {
    return !!this.verificationId;
  }

  /**
   * Identifies the challenge as it stands now. A caller with an await ahead of its send
   * captures this first and hands it back - through `send`, or through `isCurrent` on an
   * error path - so that work begun before a cancel cannot act on the panel after it.
   */
  public get epoch(): number {
    return this.generation;
  }

  public get id(): string {
    return this.verificationId;
  }

  /** The entered code, trimmed - it arrives pasted from a text message often enough. */
  public get code(): string {
    return (this.verificationCode || '').trim();
  }

  /**
   * Whether a send is possible at all: nothing in flight, and the throttle has not already
   * refused one. Surfaces bind their initial-send button to this, so a challenge Firebase
   * has already refused cannot be offered another attempt (Bug 47846).
   */
  public get canSend(): boolean {
    return !this.sendInProgress && !this.throttled;
  }

  /**
   * Whether a *re*send is possible: as canSend, plus a challenge already under way and the
   * cooldown since the last code elapsed.
   *
   * The cooldown is deliberately only here and not on canSend. An initial send is the user
   * starting something, and on every surface it costs a password check first; a resend is
   * the one button that can be pressed over and over on its own.
   */
  public get canResend(): boolean {
    return !!this.sender && this.canSend && this.cooldownRemaining === 0;
  }

  /**
   * What the Resend Code button should read. Kept here rather than in five templates so the
   * wording cannot drift between the surfaces, which is how the enrollment and removal steps
   * ended up describing the same throttle differently before 47328.
   */
  public get resendLabel(): string {
    return this.cooldownRemaining > 0
      ? `Resend Code in ${this.cooldownRemaining}s`
      : 'Resend Code';
  }

  /**
   * Starts a challenge. Resolves true when the code is out and the state was deposited,
   * false when the challenge was abandoned mid-send. `phoneHint` is the masked number to
   * show the user, where the surface has one to show.
   */
  public send(sender: MfaCodeSender, options: MfaSendOptions = {}): Promise<boolean> {
    if (options.epoch !== undefined && !this.isCurrent(options.epoch)) {
      // Abandoned before the send even started. Unlike the mid-send case there is still
      // time to not text at all, so nothing goes out.
      return Promise.resolve(false);
    }
    if (this.throttled) {
      // Firebase has already refused a send for this challenge. Another would be refused
      // too and would extend the block, so it is not attempted. Surfaces disable the button
      // as well - this is the safety net behind that, not the whole guard, since several of
      // them reauthenticate a password before reaching here and that round-trip should not
      // happen either.
      return Promise.resolve(false);
    }
    this.sender = sender;
    this.phoneHint = options.phoneHint;
    return this.text();
  }

  /**
   * Records a throttle that Firebase raised somewhere this challenge cannot see - the
   * credential check every surface runs before its send (Bug 47869). `text()` sets the flag
   * for its own refusals; this is how a caller hands over one that never reached it.
   *
   * Takes the raw error and recognises the code itself, so the rule lives here rather than
   * in each surface's error handler. Anything else is ignored, so it is safe to call from a
   * catch that sees every kind of failure.
   *
   * `epoch` is the challenge's generation as the caller captured it before the credential
   * check - the same value it hands `send`. Given one, a refusal belonging to a challenge
   * the user has since abandoned is dropped rather than marking the one that replaced it,
   * which is the guard text() already applies to this flag for its own refusals. No surface
   * can reach that today, because all of them disable Cancel while a credential check is in
   * flight; passing it keeps the property structural rather than resting on four templates.
   */
  public noteThrottled(e: any, epoch?: number): void {
    if (e?.code !== 'auth/too-many-requests') {
      return;
    }
    if (epoch !== undefined && !this.isCurrent(epoch)) {
      return;
    }
    // The surfaces reach this from a Firebase rejection, so outside Angular's zone - and
    // canSend drives a disabled binding.
    this.ngZone.run(() => {
      this.throttled = true;
    });
  }

  /**
   * Whether an epoch still describes this challenge. Callers use it to drop an error that
   * belongs to a challenge the user has since abandoned.
   */
  public isCurrent(epoch: number): boolean {
    return epoch === this.generation;
  }

  /**
   * Sends a replacement code for a challenge already under way, retiring the previous id
   * first. Resolves false when there is nothing to resend, or a send is already out.
   */
  public resend(): Promise<boolean> {
    if (!this.canResend) {
      return Promise.resolve(false);
    }
    this.verificationCode = null;
    this.verificationId = null;
    return this.text();
  }

  /**
   * Ends the challenge: any send still in flight is disowned, and the reCAPTCHA released.
   * Safe to call on a challenge that never started.
   */
  public reset(): void {
    this.generation++;
    this.codeSent = false;
    this.verificationCode = null;
    this.verificationId = null;
    this.phoneHint = null;
    this.sender = null;
    this.sendInProgress = false;
    this.throttled = false;
    // The cooldown ends with the challenge. Restarting one deliberately costs another
    // password check on every surface, which is friction enough; keeping the clock running
    // across a cancel would instead punish someone who backed out and started again.
    //
    // It does mean cancel-and-reopen is a way around the wait. Firebase's own counter is
    // server-side and keyed on the number, so that loop still walks toward the same block -
    // the cooldown slows the common case, it does not seal the door.
    this.cooldownDeadline = 0;
    this.stopCooldown();
    this.releaseRecaptcha();
  }

  /**
   * Every exit here checks the generation first. A send that has been disowned - by a
   * reset, or by a newer send started after one - shares these fields with whoever owns
   * the challenge now, so writing on the way out reaches into that owner's state rather
   * than its own.
   */
  private async text(): Promise<boolean> {
    const generation = this.generation;
    this.sendInProgress = true;
    try {
      const verificationId = await this.sender(this.freshRecaptcha());
      return this.ngZone.run(() => {
        if (!this.isCurrent(generation)) {
          return false;
        }
        this.verificationId = verificationId;
        this.codeSent = true;
        // Only a send that actually delivered starts the clock. A send that failed for any
        // other reason has cost the user nothing and should not cost them thirty seconds
        // before they can correct it and try again.
        this.startCooldown();
        return true;
      });
    } catch (e) {
      if (!this.isCurrent(generation)) {
        // The verifier this would release is no longer this send's: reset() already
        // released that one, and any newer send has since rendered its own. Clearing it
        // would break a request still in flight.
        return false;
      }
      this.ngZone.run(() => {
        this.releaseRecaptcha();
        // Only the send path can observe this. A verification rejected for the same reason
        // is the caller's to report: it consumes no send, so it does not retire the resend.
        if (e?.code === 'auth/too-many-requests') {
          this.throttled = true;
        }
      });
      throw e;
    } finally {
      this.ngZone.run(() => {
        if (this.isCurrent(generation)) {
          // Only the current send may clear the flag. A reset already cleared it, and a
          // newer send owns it - releasing it there would re-enable Resend mid-request.
          this.sendInProgress = false;
        }
      });
    }
  }

  /**
   * Starts the wait and the interval that redraws it.
   *
   * The interval runs outside Angular. A timer inside the zone would schedule change
   * detection for as long as it lived and leave the app never stable, which breaks anything
   * that waits on stability. Each tick re-enters the zone only when the displayed second
   * actually turned over, so the view is refreshed once a second rather than four times.
   *
   * Polled four times a second so the digit changes close to when the second does; a
   * one-second interval drifts visibly against the deadline it is displaying.
   */
  private startCooldown(): void {
    this.stopCooldown();
    this.cooldownDeadline = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
    this.cooldownShown = RESEND_COOLDOWN_SECONDS;
    this.ngZone.runOutsideAngular(() => {
      this.cooldownTimer = setInterval(() => this.tickCooldown(), 250);
    });
  }

  private tickCooldown(): void {
    const remaining = this.cooldownRemaining;
    if (remaining === this.cooldownShown) {
      return;
    }
    this.cooldownShown = remaining;
    if (remaining === 0) {
      this.stopCooldown();
    }
    this.ngZone.run(() => { /* the value is computed; this is the redraw */ });
  }

  private stopCooldown(): void {
    if (this.cooldownTimer !== null) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  private freshRecaptcha(): any {
    const previous = this.recaptchaVerifier;
    this.recaptchaVerifier = null;
    this.recaptchaVerifier = this.authService.createFreshRecaptchaVerifier(this.host(), previous);
    return this.recaptchaVerifier;
  }

  private releaseRecaptcha(): void {
    this.authService.clearRecaptchaVerifier(this.recaptchaVerifier);
    this.recaptchaVerifier = null;
  }
}
